// Hub3RemoteList.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { Box, Typography, Divider, List, ListItem, ListItemText, ListItemIcon, Drawer } from '@mui/material';
import { Add, MoreHoriz } from '@mui/icons-material';
import { GlobalStateContext } from '@context/GlobalContextProvider';
import { useTranslation } from 'react-i18next';
import { useRemoteCtrl } from '../api/useRemoteCtrl.js';
import { buildMatterPowerCommands } from '@/pages/personal/devices/wifi-module/ir/utils/irMatterCommands.js';

export default function MobileHub3RemoteList({ deviceUUID, editable = true, device: listDevice }) {
  const { gManageDevice } = useContext(GlobalStateContext);
  const { t } = useTranslation();
  const [device, setDevice] = useState({
    stateInfo: {
      remoteList: [],
    },
  });

  // 菜单相关状态
  const [selectedRemote, setSelectedRemote] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { gAuth, gStripe, setSnackbarValue } = useContext(GlobalStateContext);
  const { deleteIRRemote, duplicateIRRemote, addRemoteToMatter, updateLocalRemoteList } = useRemoteCtrl(
    gAuth,
    gStripe,
    setSnackbarValue
  );
  const [isDuplicating, setIsDuplicating] = useState(false);
  const singleLineTextSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  // 获取设备信息
  useEffect(() => {
    const sameDevice = (item) => item?.deviceUUID?.toUpperCase() === deviceUUID?.toUpperCase();
    const detail = sameDevice(gManageDevice.deviceStatus) ? gManageDevice.deviceStatus : null;
    const currentListDevice = listDevice || gManageDevice.companyDevices.find(sameDevice);
    const foundDevice = editable ? detail || currentListDevice : currentListDevice || detail;
    if (foundDevice) {
      const deviceWithSafeRemoteList = {
        ...foundDevice,
        stateInfo: {
          ...foundDevice.stateInfo,
          remoteList: foundDevice.stateInfo?.remoteList || [],
        },
      };
      setDevice(deviceWithSafeRemoteList);
    } else setDevice({ deviceUUID, stateInfo: { remoteList: [] } });
  }, [deviceUUID, editable, listDevice, gManageDevice.deviceStatus, gManageDevice.companyDevices]);

  // 添加遥控器的处理函数
  const handleAddRemote = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    url.searchParams.delete('keyLevel');
    url.searchParams.delete('deviceUUID');
    const params = Object.fromEntries(url.searchParams.entries());
    navigate({
      pathname: '/biz/access-control/ir-type',
      search: createSearchParams({ ...params, hub3DeviceId: device.deviceUUID }).toString(),
    });
  };

  // 处理 ListItem 点击事件
  const handleListItemClick = (remote, index) => {
    setSelectedRemote(remote);
    setSelectedIndex(index);
    setDrawerOpen(true);
  };

  const deleteRemote = (remote = selectedRemote) => {
    deleteIRRemote(device.deviceUUID, remote.uuid, (response) => {
      console.log('deleteIRRemote callback response:', response);

      if (response.success) {
        console.log('delete success:', response.data);
        updateLocalRemoteList(device.deviceUUID, remote, true);
      } else {
        console.error('delete failed:', response.message);
        setSnackbarValue({
          logScope: 'components/MobileHub3RemoteList.deleteRemote',
          logReason: 'pages.ir.remoteList.deleteFailed',
          open: true,
          msg: t('pages.ir.remoteList.deleteFailed'),
          severity: 'error',
        });
      }
    });
    setDrawerOpen(false);
  };

  // 复制遥控器：后台生成新 uuid（自学习同时复制按键与红外数据），成功后补一次 Matter 注册
  const duplicateRemote = (remote = selectedRemote) => {
    setDrawerOpen(false);
    if (!remote?.uuid || isDuplicating) return;
    setIsDuplicating(true);
    // 保持原名称，后台缺省沿用源遥控器的 alias
    duplicateIRRemote(device.deviceUUID, remote.uuid, remote.alias || '', (response) => {
      setIsDuplicating(false);
      if (!response.success || !response.data?.uuid) {
        console.error('duplicateIRRemote failed:', response.message);
        setSnackbarValue({
          open: true,
          msg: t('pages.ir.remote.duplicateFail'),
          severity: 'error',
        });
        return;
      }
      const { skippedKeys } = response.data;
      const newRemote = { ...response.data };
      delete newRemote.duplicatedKeys;
      delete newRemote.skippedKeys;
      updateLocalRemoteList(device.deviceUUID, newRemote);
      const commands = buildMatterPowerCommands(newRemote);
      if (commands) {
        addRemoteToMatter(device.deviceUUID, commands.cmdOn, commands.cmdOff, newRemote, (matterResponse) => {
          console.log('addRemoteToMatter callback response:', matterResponse);
        });
      }
      if (skippedKeys > 0) {
        setSnackbarValue({
          open: true,
          msg: t('pages.ir.remote.duplicateSkippedKeys'),
          severity: 'warning',
        });
      }
    });
  };

  const gotoRemoteControl = (remote = selectedRemote) => {
    let remotePath = '';
    let irTypeNum = parseInt(remote.type);
    if (remote.code === 0) {
      remotePath = '/biz/access-control/learn';
    } else if (irTypeNum === 0xc000) {
      remotePath = '/biz/access-control/remote-air';
    } else {
      remotePath = '/biz/access-control/remote-non-air';
    }
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    url.searchParams.delete('keyLevel');
    url.searchParams.delete('deviceUUID');
    const params = Object.fromEntries(url.searchParams.entries());
    navigate({
      pathname: remotePath,
      search: createSearchParams({
        ...params,
        hub3DeviceId: device.deviceUUID,
        remote: JSON.stringify(remote),
      }).toString(),
    });
    setDrawerOpen(false);
  };
  const remoteList = device.stateInfo?.remoteList || [];
  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
      }}
    >
      <List disablePadding>
        {remoteList.map((remote, index) => (
          <React.Fragment key={index}>
            <ListItem
              sx={{ px: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                gotoRemoteControl(remote);
              }}
            >
              <ListItemText
                sx={{ minWidth: 0 }}
                primary={<Typography sx={singleLineTextSx}>{remote.alias || `Remote ${index + 1}`}</Typography>}
              />
              {editable && (
                <ListItemIcon
                  sx={{ minWidth: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleListItemClick(remote, index);
                  }}
                >
                  <MoreHoriz />
                </ListItemIcon>
              )}
            </ListItem>
          </React.Fragment>
        ))}
        {editable && (
          <>
            {remoteList?.length > 0 && <Divider sx={{ opacity: 0.4 }} />}
            <ListItem onClick={handleAddRemote} sx={{ px: 0 }}>
              <ListItemText
                sx={{ minWidth: 0 }}
                primary={<Typography sx={singleLineTextSx}>{t('pages.ir.remote.addRemote')}</Typography>}
              />
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <Add />
              </ListItemIcon>
            </ListItem>
          </>
        )}
      </List>
      {/* 移动端底部抽屉 */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxHeight: '50vh',
          },
        }}
      >
        <Box sx={{ width: '100%', '& .MuiListItem-root': { justifyContent: 'center' } }}>
          <List>
            <ListItem>
              <Typography sx={{ color: 'rgb(204, 204, 204)', ...singleLineTextSx }}>
                {selectedRemote?.alias || `Remote ${selectedIndex + 1}`}
              </Typography>
            </ListItem>
            <ListItem onClick={() => gotoRemoteControl(selectedRemote)}>
              <Typography>{t('pages.ir.remote.detail')}</Typography>
            </ListItem>
            <ListItem onClick={() => duplicateRemote(selectedRemote)}>
              <Typography>{t('pages.ir.remote.duplicate')}</Typography>
            </ListItem>
            <ListItem onClick={() => deleteRemote(selectedRemote)}>
              <Typography color="error.main">{t('pages.ir.remote.delete')}</Typography>
            </ListItem>
            <ListItem onClick={() => setDrawerOpen(false)}>
              <Typography>{t('deviceMember.opt.cancel')}</Typography>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
